import React from 'react'
import { useState } from 'react'
import { invert } from 'lodash'

import { CardType } from 'types/deck'
import { INVERSE_PRICE_CODES, PRICE_ICON_NAMES, PRICE_COLORS, HUMAN_PRICE_SOURCE, PRICE_SOURCE } from 'types/active'

import Checkbox from 'components/formElements/Checkbox'
import BuyCardsLink from 'components/formElements/BuyCardsLink'
import PhatButton from 'components/formElements/PhatButton'
import Icon from 'components/elements/Icon'

import { getPriceStringFromFloat } from 'utils/Price'

import styles from './buyCardsFromMultipileVendors.module.scss'

type Props = {
  cards: CardType[]
  linkedFrom: string
}

const BuyFromMultipleVendors = ({ cards, linkedFrom }: Props) => {
  const [selectedPriceSources, setSelectedPriceSources] = useState<Record<string, boolean>>({
    [PRICE_SOURCE.CK]: true,
    [PRICE_SOURCE.TCG]: true,
    [PRICE_SOURCE.MP]: true,
    [PRICE_SOURCE.SCG]: true,
  })

  const HUMAN_PRICE_NAMES = invert(HUMAN_PRICE_SOURCE)

  const minPricesPerCard = cards.reduce(
    (fullListAcc, card) => {
      // get minimum prices for this card for each selected vendor
      const minPricesForSelectedSources = Object.entries(card.prices)
        .filter(([key, _value]) => selectedPriceSources[INVERSE_PRICE_CODES[key]])
        .map(([key, _value]) => {
          return { minPrice: Number(card.prices[`${key}Minimum`]), source: key }
        })

      // determine which vendor has the cheapest price for this card
      const minPrice = minPricesForSelectedSources.reduce(
        (acc, source) => {
          if (source.minPrice === 0) return acc

          if (source.minPrice < acc.minPrice || acc.minPrice === 0) {
            acc.source = source.source
            acc.minPrice = source.minPrice
          }

          return acc
        },
        { source: '', minPrice: 0 } as { source: string; minPrice: number },
      )

      // add this card to the cheapest vendors list
      if (minPrice.source === '') return fullListAcc
      fullListAcc[minPrice.source] = [...(fullListAcc[minPrice.source] || []), card]

      return fullListAcc
    },
    {} as Record<string, CardType[]>,
  )

  return (
    <div>
      <p>
        Select your preferred vendors below, and we'll generate links to wherever any printing is the least expensive.
      </p>
      <div className={styles.checkboxGrid}>
        <Checkbox
          className={styles.nowrap}
          key={`ck-checkbox`}
          label="Card Kingdom ($)"
          checked={selectedPriceSources[PRICE_SOURCE.CK]}
          onChange={() =>
            setSelectedPriceSources({
              ...selectedPriceSources,
              [PRICE_SOURCE.CK]: !selectedPriceSources[PRICE_SOURCE.CK],
            })
          }
        />
        <Checkbox
          className={styles.nowrap}
          key={`tcg-checkbox`}
          label="TCGplayer ($)"
          checked={selectedPriceSources[PRICE_SOURCE.TCG]}
          onChange={() =>
            setSelectedPriceSources({
              ...selectedPriceSources,
              [PRICE_SOURCE.TCG]: !selectedPriceSources[PRICE_SOURCE.TCG],
            })
          }
        />
        <Checkbox
          className={styles.nowrap}
          key={`scg-checkbox`}
          label="Star City Games ($)"
          checked={selectedPriceSources[PRICE_SOURCE.SCG]}
          onChange={() =>
            setSelectedPriceSources({
              ...selectedPriceSources,
              [PRICE_SOURCE.SCG]: !selectedPriceSources[PRICE_SOURCE.SCG],
            })
          }
        />
        <Checkbox
          className={styles.nowrap}
          key={`mp-checkbox`}
          label="Mana Pool ($)"
          checked={selectedPriceSources[PRICE_SOURCE.MP]}
          onChange={() =>
            setSelectedPriceSources({
              ...selectedPriceSources,
              [PRICE_SOURCE.MP]: !selectedPriceSources[PRICE_SOURCE.MP],
            })
          }
        />
      </div>
      <div className={styles.buttonGrid}>
        {['ck', 'tcg', 'scg', 'mp'].map(source => {
          const priceSourceCode = Number(INVERSE_PRICE_CODES[source])
          const price =
            minPricesPerCard[source]?.reduce((acc, card) => (acc += card.prices[`${source}Minimum`]), 0) || 0
          return (
            <BuyCardsLink
              key={`${source}-buylink`}
              priceSource={priceSourceCode}
              cards={minPricesPerCard[source] || []}
              linkedFrom={linkedFrom}
              isDisabled={!selectedPriceSources[PRICE_SOURCE.SCG]}>
              <PhatButton
                className={styles.retailerButton}
                disabled={!Object.keys(minPricesPerCard).includes(source)}
                style={{ backgroundColor: PRICE_COLORS[priceSourceCode] }}>
                <Icon className={PRICE_ICON_NAMES[priceSourceCode]} />
                {HUMAN_PRICE_NAMES[priceSourceCode]} {getPriceStringFromFloat(price, priceSourceCode)} (
                {minPricesPerCard[source]?.length || 0} / {cards.length} cards)
              </PhatButton>
            </BuyCardsLink>
          )
        })}
      </div>
    </div>
  )
}

export default BuyFromMultipleVendors
